import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Bell,
  Check,
  X,
  CheckCheck,
  AlertCircle,
  Loader2,
  CalendarDays,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api-client";
import { formatDistanceToNow, format } from "date-fns";
import { es } from "date-fns/locale";
import { useGlobalToast } from "../contexts/ToastContext";
import { useLocation } from "@/contexts/LocationContext";
import WebPushToggle from "./WebPushToggle";
import {
  getNotificationTypeVisual,
  getNotificationLink,
  getChangeRequestReservationLink,
} from "@/lib/notifications";

// Warning que puede venir en la respuesta de aprobar-cambio de una reserva
// cuando la mesa asignada está bloqueada (TableBlock) o pertenece a un salón
// (Room) deshabilitado ese día. Aviso SOFT para staff en ambos casos: el
// backend lo permite igualmente, solo avisa.
interface ReservationWarning {
  code: string;
  tableId?: string;
  tableName?: string;
  reason?: string | null;
  shift?: string | null;
  roomId?: string;
  roomName?: string;
}

interface ReservationApiResponse {
  success: boolean;
  data?: unknown;
  warnings?: ReservationWarning[];
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, any> | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

interface NotificationsDropdownProps {
  className?: string;
}

export default function NotificationsDropdown({
  className,
}: NotificationsDropdownProps) {
  const { showToast } = useGlobalToast();
  // Sede con el módulo de clases: la campanita enseña SOLO lo de clases
  // (contador y listado filtrados por tipo en el servidor, vía `?type=`).
  const { currentLocation } = useLocation();
  const classesOnly = currentLocation?.classesEnabled === true;
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Change request modal state
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [actionLoading, setActionLoading] = useState<
    "approve" | "reject" | null
  >(null);
  const [rejectReason, setRejectReason] = useState("");
  const [changeAlreadyProcessed, setChangeAlreadyProcessed] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  // Botón "Ver reserva" del modal de solicitud de cambio.
  const changeRequestLink = getChangeRequestReservationLink(
    selectedNotification?.data,
  );
  // Cliente de la reserva de la solicitud (se rellena con el fetch que ya
  // comprueba si sigue pendiente): así el modal encabeza con nombre y teléfono
  // en vez de datos sueltos.
  const [changeCustomer, setChangeCustomer] = useState<{
    name: string;
    phone: string | null;
  } | null>(null);

  // Fetch unread count (silently fails on network errors to avoid console spam)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get("/notifications/count", {
        params: classesOnly ? { type: "new_class_booking" } : {},
      });
      if (response.data.success) {
        setUnreadCount(response.data.data.count);
      }
    } catch {
      // Silently ignore network errors during polling
      // This prevents console spam when connection is unstable
    }
  }, [classesOnly]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!isOpen) return;

    setLoading(true);
    try {
      const response = await api.get("/notifications", {
        params: {
          limit: 10,
          ...(classesOnly ? { type: "new_class_booking" } : {}),
        },
      });
      if (response.data.success) {
        setNotifications(response.data.data);
      }
    } catch {
      // Silently ignore network errors
    } finally {
      setLoading(false);
    }
  }, [isOpen, classesOnly]);

  // Poll for new notifications every 30 seconds.
  //
  // Solo mientras la pestaña está visible: este contador era, con diferencia,
  // la petición más frecuente contra la API, y se seguía pidiendo cada 30 s en
  // todas las pestañas abiertas en segundo plano (en el log del 22-jul salían
  // en ráfagas de 2-3 llamadas simultáneas, una por pestaña, durante horas).
  // Al volver a la pestaña se refresca de inmediato, así que el contador nunca
  // se ve desactualizado.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const startPolling = () => {
      if (interval !== null) return;
      interval = setInterval(fetchUnreadCount, 30000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchUnreadCount();
        startPolling();
      } else {
        stopPolling();
      }
    };

    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchUnreadCount]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      await api.post("/notifications/mark-read", {
        notificationIds: [notificationId],
      });
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        })),
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    // If it's a change request, check if still pending and show the modal
    if (
      notification.type === "reservation_change_request" &&
      notification.data?.reservationId
    ) {
      setSelectedNotification(notification);
      setCheckingStatus(true);
      setShowChangeModal(true);
      setRejectReason("");
      setChangeAlreadyProcessed(false);
      setChangeCustomer(null);
      setIsOpen(false);

      // Check if the reservation still has pending changes
      try {
        const response = await api.get(
          `/reservations/${notification.data.reservationId}`,
        );
        if (response.data.success) {
          const reservation = response.data.data;
          if (reservation.customer) {
            setChangeCustomer({
              name: `${reservation.customer.firstName || ""} ${reservation.customer.lastName || ""}`.trim(),
              phone: reservation.customer.phone || null,
            });
          }
          // If no pending change request, it was already processed
          if (!reservation.pendingChangeRequest) {
            setChangeAlreadyProcessed(true);
          }
        }
      } catch (error) {
        // If error fetching, assume already processed
        setChangeAlreadyProcessed(true);
      } finally {
        setCheckingStatus(false);
      }
      return;
    }

    setIsOpen(false);
  };

  // Approve change request
  const handleApproveChange = async () => {
    if (!selectedNotification?.data?.reservationId) return;

    setActionLoading("approve");
    try {
      const response = await api.post<ReservationApiResponse>(
        `/reservations/${selectedNotification.data.reservationId}/approve-change`,
      );
      setShowChangeModal(false);
      setSelectedNotification(null);
      (response.data.warnings || []).forEach((w) => {
        if (w.code === "table_blocked") {
          const tableName = w.tableName || "seleccionada";
          const reasonSuffix = w.reason ? ` (${w.reason})` : "";
          showToast(
            `Cambio aprobado en la mesa ${tableName}, que está bloqueada${reasonSuffix}.`,
            "info",
          );
        } else if (w.code === "room_disabled") {
          const tableName = w.tableName || "seleccionada";
          const roomName = w.roomName || "de esta mesa";
          showToast(
            `Cambio aprobado en la mesa ${tableName}: el salón ${roomName} está deshabilitado ese día.`,
            "info",
          );
        }
      });
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error approving change:", error);
    } finally {
      setActionLoading(null);
    }
  };

  // Reject change request
  const handleRejectChange = async () => {
    if (!selectedNotification?.data?.reservationId) return;

    setActionLoading("reject");
    try {
      await api.post(
        `/reservations/${selectedNotification.data.reservationId}/reject-change`,
        {
          reason: rejectReason,
        },
      );
      setShowChangeModal(false);
      setSelectedNotification(null);
      setRejectReason("");
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error rejecting change:", error);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <div ref={dropdownRef} className={`relative ${className || ""}`}>
        {/* Bell Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 relative transition-colors"
          aria-label="Notificaciones"
        >
          <Bell size={20} />
          {/* Badge */}
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {isOpen && (
          <>
            {/* Backdrop for mobile */}
            <div
              className="fixed inset-0 z-40 md:hidden"
              onClick={() => setIsOpen(false)}
            />

            <div className="fixed inset-x-2 top-[calc(64px+var(--safe-top))] sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 w-auto sm:w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Notificaciones</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <CheckCheck size={14} />
                    Marcar todas como leídas
                  </button>
                )}
              </div>

              {/* Notifications List */}
              <div className="max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full mx-auto mb-2" />
                    Cargando...
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No tienes notificaciones</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {notifications.map((notification) => {
                      const link = getNotificationLink(notification);
                      const { icon: TypeIcon, colorClass } =
                        getNotificationTypeVisual(notification.type, notification);
                      const content = (
                        <div
                          className={`flex gap-3 p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                            !notification.isRead ? "bg-primary-50/50" : ""
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          {/* Icon */}
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                              <TypeIcon className={`w-4 h-4 ${colorClass}`} />
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p
                                className={`text-sm ${
                                  !notification.isRead
                                    ? "font-semibold text-gray-900"
                                    : "font-medium text-gray-700"
                                }`}
                              >
                                {notification.title}
                              </p>
                              {!notification.isRead && (
                                <span className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-1.5" />
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatDistanceToNow(
                                new Date(notification.createdAt),
                                {
                                  addSuffix: true,
                                  locale: es,
                                },
                              )}
                            </p>
                          </div>
                        </div>
                      );

                      if (link) {
                        return (
                          <Link key={notification.id} to={link}>
                            {content}
                          </Link>
                        );
                      }

                      return <div key={notification.id}>{content}</div>;
                    })}
                  </div>
                )}
              </div>

              <WebPushToggle />

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="border-t border-gray-100 px-4 py-2">
                  <Link
                    to="/notifications/history"
                    onClick={() => setIsOpen(false)}
                    className="block text-center text-sm text-primary-600 hover:text-primary-700 font-medium py-1"
                  >
                    Ver todas las notificaciones
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal de solicitud de cambio - renderizado con portal */}
      {showChangeModal &&
        selectedNotification &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold">Solicitud de cambio</h3>
                <p className="text-white/80 text-sm mt-1">
                  Un cliente quiere modificar su reserva
                </p>
              </div>

              <div className="p-6 space-y-4">
                {checkingStatus ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-2" />
                    <p className="text-gray-600">Verificando estado...</p>
                  </div>
                ) : changeAlreadyProcessed ? (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCheck className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Solicitud ya procesada
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Esta solicitud de cambio ya ha sido contestada
                      anteriormente.
                    </p>
                    {changeRequestLink && (
                      <Link
                        to={changeRequestLink}
                        onClick={() => {
                          setShowChangeModal(false);
                          setSelectedNotification(null);
                        }}
                        className="mt-6 w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        <CalendarDays className="w-4 h-4" />
                        Ver reserva
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        setShowChangeModal(false);
                        setSelectedNotification(null);
                      }}
                      className="mt-3 w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                    >
                      Cerrar
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Cliente: nombre y teléfono arriba, en una sola pieza —
                        antes el modal solo mostraba fecha/hora/personas
                        sueltas y no se sabía DE QUIÉN era la reserva. */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-base font-semibold text-gray-900 leading-snug">
                        {changeCustomer?.name ||
                          (selectedNotification.data
                            ?.customerName as string) ||
                          "Cliente"}
                      </p>
                      {changeCustomer?.phone && (
                        <a
                          href={`tel:${changeCustomer.phone}`}
                          className="text-sm text-primary-600 hover:underline"
                        >
                          📞 {changeCustomer.phone}
                        </a>
                      )}
                    </div>

                    {/* Comparativa actual → solicitado, fila a fila: se lee de
                        un vistazo también en móvil. */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-3 text-[11px] uppercase font-medium text-blue-600 px-4 pt-3 pb-1">
                        <span></span>
                        <span>Actual</span>
                        <span>Solicitado</span>
                      </div>
                      {(() => {
                        const data = selectedNotification.data;
                        const req = data?.requestedChanges as
                          | {
                              date?: string;
                              time?: string;
                              partySize?: number;
                            }
                          | undefined;
                        const fmt = (v: string | undefined | null) =>
                          v ? format(new Date(v), "dd/MM/yyyy") : "—";
                        const rows: Array<{
                          label: string;
                          current: string;
                          requested: string | null;
                        }> = [
                          {
                            label: "📅 Día",
                            current: fmt(data?.currentDate),
                            requested: req?.date ? fmt(req.date) : null,
                          },
                          {
                            label: "🕐 Hora",
                            current: data?.currentTime || "—",
                            requested: req?.time || null,
                          },
                          {
                            label: "👥 Pers.",
                            current: String(data?.currentPartySize ?? "—"),
                            requested:
                              req?.partySize != null
                                ? String(req.partySize)
                                : null,
                          },
                        ];
                        return rows.map((row) => (
                          <div
                            key={row.label}
                            className={`grid grid-cols-3 items-center px-4 py-1.5 text-sm ${
                              row.requested ? "bg-blue-100/60" : ""
                            }`}
                          >
                            <span className="text-blue-900 font-medium">
                              {row.label}
                            </span>
                            <span
                              className={
                                row.requested
                                  ? "text-blue-700/70 line-through"
                                  : "text-blue-900"
                              }
                            >
                              {row.current}
                            </span>
                            <span className="font-bold text-blue-900">
                              {row.requested ?? "— igual —"}
                            </span>
                          </div>
                        ));
                      })()}
                      <div className="h-2" />
                    </div>

                    {/* Ir a la reserva: para revisarla (mesa, notas, otras
                        reservas del día) antes de decidir, o tras aprobar. */}
                    {changeRequestLink && (
                      <Link
                        to={changeRequestLink}
                        onClick={() => {
                          setShowChangeModal(false);
                          setSelectedNotification(null);
                          setRejectReason("");
                        }}
                        className="w-full py-2.5 px-4 border border-primary-300 text-primary-700 hover:bg-primary-50 font-medium rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
                      >
                        <CalendarDays className="w-4 h-4" />
                        Ver reserva en el listado
                      </Link>
                    )}

                    {/* Reject reason (optional) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Motivo de rechazo (opcional)
                      </label>
                      <input
                        type="text"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Ej: No tenemos disponibilidad en esa fecha"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={handleRejectChange}
                        disabled={actionLoading !== null}
                        className="flex-1 py-3 px-4 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {actionLoading === "reject" ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                        Rechazar
                      </button>
                      <button
                        onClick={handleApproveChange}
                        disabled={actionLoading !== null}
                        className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {actionLoading === "approve" ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Aprobar
                      </button>
                    </div>

                    {/* Cancel button */}
                    <button
                      onClick={() => {
                        setShowChangeModal(false);
                        setSelectedNotification(null);
                        setRejectReason("");
                      }}
                      disabled={actionLoading !== null}
                      className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      Cerrar sin actuar
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
